// Conditions along with follow-up questions and answers
// provided to us by GoodCanna for their V2 API.
export type FollowUpQuestion = {
  id: string;
  question: string;
  subQuestion: string;
  options: string[];
};

export type ConditionQuestionSet = {
  condition: string;
  questions: FollowUpQuestion[];
};

export const GOOD_CANNA_V2_CONDITIONS: ConditionQuestionSet[] = [
  {
    condition: 'Pain (R52)',
    questions: [
      {
        id: 'Q1',
        question: 'Where is your pain located?',
        subQuestion: 'Pain location and distribution',
        options: ['Localized to one area', 'Multiple areas involved', 'Widespread or radiating pain'],
      },
      {
        id: 'Q2',
        question: 'How long have you had this pain?',
        subQuestion: 'Duration and chronicity',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q3',
        question: 'How severe is your pain (0-10)?',
        subQuestion: 'Pain severity',
        options: ['Mild (1–3/10)', 'Moderate (4–6/10)', 'Severe (7–10/10)'],
      },
      {
        id: 'Q4',
        question: 'What treatments have you tried?',
        subQuestion: 'Prior treatment history',
        options: ['Conservative measures only', 'Medication trial(s)', 'Multiple treatments with limited relief'],
      },
      {
        id: 'Q5',
        question: 'Does the pain limit daily activities?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
  {
    condition: 'ADHD (F90)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed with ADHD?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Are you currently on medication?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q3',
        question: 'Do you have difficulty focusing daily?',
        subQuestion: 'Frequency of attention and concentration problems',
        options: ['Occasional focus issues', 'Frequent focus issues', 'Daily significant focus difficulty'],
      },
      {
        id: 'Q4',
        question: 'Are symptoms impacting work or school?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
      {
        id: 'Q5',
        question: 'Have you tried behavioral therapy?',
        subQuestion: 'Non-medication treatment history',
        options: [
          'No prior therapy',
          'Tried therapy with partial benefit',
          'Ongoing or prior therapy with limited relief',
        ],
      },
    ],
  },
  {
    condition: 'Anxiety (F41.9)',
    questions: [
      {
        id: 'Q1',
        question: 'How often do you feel anxious?',
        subQuestion: 'Symptom frequency',
        options: ['Occasional episodes', 'Frequent episodes', 'Daily or near-daily symptoms'],
      },
      {
        id: 'Q2',
        question: 'Do you experience panic attacks?',
        subQuestion: 'Panic symptom frequency',
        options: ['None or rare', 'Intermittent panic attacks', 'Frequent or severe panic attacks'],
      },
      {
        id: 'Q3',
        question: 'Are you taking any medications?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q4',
        question: 'Does anxiety affect daily functioning?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
      {
        id: 'Q5',
        question: 'Have you tried therapy or counseling?',
        subQuestion: 'Non-medication treatment history',
        options: [
          'No prior therapy',
          'Tried therapy with partial benefit',
          'Ongoing or prior therapy with limited relief',
        ],
      },
    ],
  },
  {
    condition: 'Insomnia (G47.00)',
    questions: [
      {
        id: 'Q1',
        question: 'How many hours do you sleep per night?',
        subQuestion: 'Average nightly sleep duration',
        options: ['6–7 hours/night', '4–5 hours/night', 'Less than 4 hours/night'],
      },
      {
        id: 'Q2',
        question: 'How long have you had sleep issues?',
        subQuestion: 'Duration and chronicity',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q3',
        question: 'Do you have trouble falling or staying asleep?',
        subQuestion: 'Type of insomnia pattern',
        options: ['Trouble falling asleep', 'Trouble staying asleep', 'Both falling and staying asleep'],
      },
      {
        id: 'Q4',
        question: 'Have you tried sleep medications?',
        subQuestion: 'Prior treatment exposure',
        options: ['No prior treatment', 'Some prior treatment', 'Multiple prior treatments with limited relief'],
      },
      {
        id: 'Q5',
        question: 'Does lack of sleep affect your day?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
  {
    condition: 'Arthritis (M19.90)',
    questions: [
      {
        id: 'Q1',
        question: 'Which joints are affected?',
        subQuestion: 'Joint involvement and body distribution',
        options: ['One major joint', 'Several joints involved', 'Multiple joints / bilateral involvement'],
      },
      {
        id: 'Q2',
        question: 'How long have you had symptoms?',
        subQuestion: 'Duration and chronicity',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q3',
        question: 'Do you experience stiffness or swelling?',
        subQuestion: 'Inflammatory joint symptom burden',
        options: [
          'Mild stiffness / swelling',
          'Moderate recurring stiffness / swelling',
          'Severe persistent stiffness / swelling',
        ],
      },
      {
        id: 'Q4',
        question: 'What treatments have you tried?',
        subQuestion: 'Prior treatment history',
        options: ['Conservative measures only', 'Medication trial(s)', 'Multiple treatments with limited relief'],
      },
      {
        id: 'Q5',
        question: 'Does it limit mobility?',
        subQuestion: 'Functional impact',
        options: ['Mild limitation', 'Moderate limitation', 'Significant mobility limitation'],
      },
    ],
  },
  {
    condition: 'Cancer (C00-C50)',
    questions: [
      {
        id: 'Q1',
        question: 'What type of cancer were you diagnosed with?',
        subQuestion: 'Cancer diagnosis category',
        options: ['Solid tumor history', 'Blood / hematologic cancer', 'Advanced or metastatic cancer history'],
      },
      {
        id: 'Q2',
        question: 'Are you currently receiving treatment?',
        subQuestion: 'Level of active medical follow-up',
        options: ['Not in active treatment', 'Routine follow-up in place', 'Active specialist management'],
      },
      {
        id: 'Q3',
        question: 'Do you experience pain, nausea, or appetite loss?',
        subQuestion: 'Cancer-related symptom burden',
        options: ['One symptom present', 'Multiple symptoms present', 'Persistent multi-symptom burden'],
      },
      {
        id: 'Q4',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q5',
        question: 'Are symptoms affecting daily life?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
  {
    condition: "Crohn's Disease (K50.90)",
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Do you have frequent flare-ups?',
        subQuestion: 'Flare frequency',
        options: ['Rare flare-ups', 'Intermittent flare-ups', 'Frequent or severe flare-ups'],
      },
      {
        id: 'Q3',
        question: 'Do you experience abdominal pain or diarrhea?',
        subQuestion: 'GI symptom pattern',
        options: ['Mild intermittent symptoms', 'Frequent abdominal pain / diarrhea', 'Severe ongoing GI symptoms'],
      },
      {
        id: 'Q4',
        question: 'Are you on medication?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Have symptoms worsened recently?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
    ],
  },
  {
    condition: 'Depression (F32.9)',
    questions: [
      {
        id: 'Q1',
        question: 'How often do you feel depressed?',
        subQuestion: 'Symptom frequency',
        options: ['Occasional episodes', 'Frequent episodes', 'Daily or near-daily symptoms'],
      },
      {
        id: 'Q2',
        question: 'Are you taking antidepressants?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q3',
        question: 'Do you have low energy or motivation?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q4',
        question: 'Have you tried therapy?',
        subQuestion: 'Non-medication treatment history',
        options: [
          'No prior therapy',
          'Tried therapy with partial benefit',
          'Ongoing or prior therapy with limited relief',
        ],
      },
      {
        id: 'Q5',
        question: 'Does it affect daily functioning?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
  {
    condition: 'Epilepsy (G40)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'How frequent are seizures?',
        subQuestion: 'Seizure frequency',
        options: ['Rare episodes', 'Intermittent episodes', 'Frequent or uncontrolled episodes'],
      },
      {
        id: 'Q3',
        question: 'Are you on anti-seizure medication?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q4',
        question: 'When was your last seizure?',
        subQuestion: 'Recency of most recent event',
        options: ['No recent episode', 'Episode within recent months', 'Episode very recently'],
      },
      {
        id: 'Q5',
        question: 'Are symptoms controlled?',
        subQuestion: 'Current level of control or stability',
        options: ['Generally stable', 'Partially controlled', 'Poorly controlled / worsening'],
      },
    ],
  },
  {
    condition: 'Glaucoma (H40.89)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Are you using eye drops or treatment?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q3',
        question: 'Do you have vision issues?',
        subQuestion: 'Visual symptom severity',
        options: ['Mild visual changes', 'Noticeable visual difficulty', 'Progressive vision impairment'],
      },
      {
        id: 'Q4',
        question: 'Are you under ophthalmology care?',
        subQuestion: 'Level of active medical follow-up',
        options: ['Not in active treatment', 'Routine follow-up in place', 'Active specialist management'],
      },
      {
        id: 'Q5',
        question: 'Has vision worsened?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
    ],
  },
  {
    condition: 'HIV/AIDS (B20)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Are you on antiretroviral therapy?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q3',
        question: 'Do you have weight loss or appetite issues?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q4',
        question: 'Are symptoms stable?',
        subQuestion: 'Current level of control or stability',
        options: ['Generally stable', 'Partially controlled', 'Poorly controlled / worsening'],
      },
      {
        id: 'Q5',
        question: 'Are you under physician care?',
        subQuestion: 'Level of active medical follow-up',
        options: ['Not in active treatment', 'Routine follow-up in place', 'Active specialist management'],
      },
    ],
  },
  {
    condition: 'Loss Of Appetite (R63.0)',
    questions: [
      {
        id: 'Q1',
        question: 'How long have you had appetite loss?',
        subQuestion: 'Duration and chronicity',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q2',
        question: 'Have you lost weight?',
        subQuestion: 'Presence of unintentional weight loss',
        options: ['No meaningful weight loss', 'Mild weight loss', 'Noticeable weight loss'],
      },
      {
        id: 'Q3',
        question: 'Do you feel nauseous?',
        subQuestion: 'Nausea burden',
        options: ['Occasional nausea', 'Frequent nausea', 'Persistent nausea'],
      },
      {
        id: 'Q4',
        question: 'Are you eating regularly?',
        subQuestion: 'Current oral intake pattern',
        options: ['Eating most meals', 'Reduced intake on some days', 'Poor intake on most days'],
      },
      {
        id: 'Q5',
        question: 'Have you tried treatments?',
        subQuestion: 'Prior treatment history',
        options: ['No treatment yet', 'Tried some treatments', 'Multiple treatments with limited relief'],
      },
    ],
  },
  {
    condition: 'Multiple Sclerosis (G35.9)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Do you have muscle weakness or fatigue?',
        subQuestion: 'Neuromuscular symptom burden',
        options: [
          'Mild intermittent weakness / fatigue',
          'Frequent weakness / fatigue',
          'Marked ongoing weakness / fatigue',
        ],
      },
      {
        id: 'Q3',
        question: 'Are you on treatment?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current treatment', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q4',
        question: 'Do you have flare-ups?',
        subQuestion: 'Flare frequency',
        options: ['Rare flare-ups', 'Intermittent flare-ups', 'Frequent or severe flare-ups'],
      },
      {
        id: 'Q5',
        question: 'Does it impact mobility?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
    ],
  },
  {
    condition: 'Muscle Spasms (R25.2)',
    questions: [
      {
        id: 'Q1',
        question: 'Where do spasms occur?',
        subQuestion: 'Spasm location and distribution',
        options: ['Single area involved', 'Several areas involved', 'Widespread muscle involvement'],
      },
      {
        id: 'Q2',
        question: 'How often do they happen?',
        subQuestion: 'Symptom frequency',
        options: ['Occasional episodes', 'Frequent episodes', 'Daily or near-daily symptoms'],
      },
      {
        id: 'Q3',
        question: 'How severe are they?',
        subQuestion: 'Symptom severity',
        options: ['Mild', 'Moderate', 'Severe'],
      },
      {
        id: 'Q4',
        question: 'Have you tried medications?',
        subQuestion: 'Prior treatment exposure',
        options: ['No prior treatment', 'Some prior treatment', 'Multiple prior treatments with limited relief'],
      },
      {
        id: 'Q5',
        question: 'Do they limit movement?',
        subQuestion: 'Movement limitation',
        options: ['Mild limitation', 'Moderate limitation', 'Significant movement limitation'],
      },
    ],
  },
  {
    condition: 'Neuropathy (G60.9)',
    questions: [
      {
        id: 'Q1',
        question: 'Where do you feel numbness or tingling?',
        subQuestion: 'Neuropathy distribution',
        options: ['Localized area only', 'Hands or feet primarily', 'Multiple limbs / progressive spread'],
      },
      {
        id: 'Q2',
        question: 'How long have symptoms been present?',
        subQuestion: 'Duration and chronicity',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q3',
        question: 'Are symptoms worsening?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
      {
        id: 'Q4',
        question: 'Are you on treatment?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current treatment', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Does it affect walking or sleep?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
    ],
  },
  {
    condition: "Parkinson's Disease (G20)",
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Do you have tremors or stiffness?',
        subQuestion: 'Motor symptom burden',
        options: ['Mild motor symptoms', 'Moderate tremor / stiffness', 'Marked tremor / stiffness'],
      },
      {
        id: 'Q3',
        question: 'Are you on medication?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q4',
        question: 'Do symptoms affect daily life?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
      {
        id: 'Q5',
        question: 'Have symptoms progressed?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
    ],
  },
  {
    condition: 'PTSD (F43.1)',
    questions: [
      {
        id: 'Q1',
        question: 'Have you been diagnosed with PTSD?',
        subQuestion: 'Diagnosis status and trauma-related clinical history',
        options: [
          'Formal PTSD diagnosis',
          'Prior evaluation / trauma history',
          'No formal diagnosis but symptoms reported',
        ],
      },
      {
        id: 'Q2',
        question: 'Do you experience flashbacks or nightmares?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q3',
        question: 'Are you in therapy?',
        subQuestion: 'Non-medication treatment history',
        options: [
          'No prior therapy',
          'Tried therapy with partial benefit',
          'Ongoing or prior therapy with limited relief',
        ],
      },
      {
        id: 'Q4',
        question: 'Are you taking medications?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Does it impact daily functioning?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
  {
    condition: 'Seizures (G40.409)',
    questions: [
      {
        id: 'Q1',
        question: 'How often do seizures occur?',
        subQuestion: 'Seizure frequency',
        options: ['Rare episodes', 'Intermittent episodes', 'Frequent or uncontrolled episodes'],
      },
      {
        id: 'Q2',
        question: 'When was your last episode?',
        subQuestion: 'Recency of most recent event',
        options: ['No recent episode', 'Episode within recent months', 'Episode very recently'],
      },
      {
        id: 'Q3',
        question: 'Are you on medication?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q4',
        question: 'Are seizures controlled?',
        subQuestion: 'Current level of control or stability',
        options: ['Generally stable', 'Partially controlled', 'Poorly controlled / worsening'],
      },
      {
        id: 'Q5',
        question: 'Do they impact daily life?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
    ],
  },
  {
    condition: 'Tremors (R25.1)',
    questions: [
      {
        id: 'Q1',
        question: 'Where do you experience tremors?',
        subQuestion: 'Tremor distribution',
        options: ['Single body area', 'Upper extremities mainly', 'Multiple areas / generalized tremor'],
      },
      {
        id: 'Q2',
        question: 'How often do they occur?',
        subQuestion: 'Symptom frequency',
        options: ['Occasional episodes', 'Frequent episodes', 'Daily or near-daily symptoms'],
      },
      {
        id: 'Q3',
        question: 'Do they interfere with daily tasks?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
      {
        id: 'Q4',
        question: 'Are you on treatment?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current treatment', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Have symptoms worsened?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
    ],
  },
  {
    condition: 'Autism (F84.0)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Do you have sensory sensitivities?',
        subQuestion: 'Sensory sensitivity burden',
        options: ['Mild sensitivities', 'Moderate sensitivities', 'Marked sensory sensitivities'],
      },
      {
        id: 'Q3',
        question: 'Do symptoms affect daily functioning?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q4',
        question: 'Are you receiving therapy?',
        subQuestion: 'Non-medication treatment history',
        options: [
          'No prior therapy',
          'Tried therapy with partial benefit',
          'Ongoing or prior therapy with limited relief',
        ],
      },
      {
        id: 'Q5',
        question: 'Do you have behavioral challenges?',
        subQuestion: 'Behavioral regulation concerns',
        options: [
          'Mild / occasional challenges',
          'Moderate recurrent challenges',
          'Significant behavioral dysregulation',
        ],
      },
    ],
  },
  {
    condition: 'Intractable Pain (R52.1)',
    questions: [
      {
        id: 'Q1',
        question: 'How long have you had this pain (6+ months)?',
        subQuestion: 'Duration and chronicity',
        options: ['6–12 months', '1–3 years', 'More than 3 years'],
      },
      {
        id: 'Q2',
        question: 'What treatments have failed?',
        subQuestion: 'Failed treatment burden',
        options: ['Basic measures failed', 'Medication trial(s) failed', 'Multiple therapies failed'],
      },
      {
        id: 'Q3',
        question: 'How severe is pain?',
        subQuestion: 'Pain severity',
        options: ['Mild (1–3/10)', 'Moderate (4–6/10)', 'Severe (7–10/10)'],
      },
      {
        id: 'Q4',
        question: 'Does it affect daily life?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
      {
        id: 'Q5',
        question: 'Have you seen specialists?',
        subQuestion: 'Specialist involvement',
        options: ['No specialist care yet', 'Seen specialist previously', 'Active specialist follow-up'],
      },
    ],
  },
  {
    condition: 'Hepatitis C (B19.2)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Are you receiving treatment?',
        subQuestion: 'Level of active medical follow-up',
        options: ['Not in active treatment', 'Routine follow-up in place', 'Active specialist management'],
      },
      {
        id: 'Q3',
        question: 'Do you have fatigue or appetite loss?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q4',
        question: 'Are symptoms stable?',
        subQuestion: 'Current level of control or stability',
        options: ['Generally stable', 'Partially controlled', 'Poorly controlled / worsening'],
      },
      {
        id: 'Q5',
        question: 'Are you under physician care?',
        subQuestion: 'Level of active medical follow-up',
        options: ['Not in active treatment', 'Routine follow-up in place', 'Active specialist management'],
      },
    ],
  },
  {
    condition: 'ALS (G12.21)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Do you have muscle weakness?',
        subQuestion: 'Motor weakness severity',
        options: ['Mild weakness', 'Moderate weakness', 'Marked progressive weakness'],
      },
      {
        id: 'Q3',
        question: 'Has condition progressed?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
      {
        id: 'Q4',
        question: 'Are you under specialist care?',
        subQuestion: 'Level of active medical follow-up',
        options: ['Not in active treatment', 'Routine follow-up in place', 'Active specialist management'],
      },
      {
        id: 'Q5',
        question: 'Does it affect mobility or speech?',
        subQuestion: 'Functional impact',
        options: ['Mild limitation', 'Moderate limitation', 'Marked mobility / speech impairment'],
      },
    ],
  },
  {
    condition: "Tourette's (F95.2)",
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'How frequent are tics?',
        subQuestion: 'Tic frequency',
        options: ['Occasional tics', 'Frequent tics', 'Near-daily / severe tics'],
      },
      {
        id: 'Q3',
        question: 'Do they interfere with daily life?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
      {
        id: 'Q4',
        question: 'Are you on treatment?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current treatment', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Have symptoms worsened?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
    ],
  },
  {
    condition: 'Ulcerative Colitis (K51.90)',
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Do you have flare-ups?',
        subQuestion: 'Flare frequency',
        options: ['Rare flare-ups', 'Intermittent flare-ups', 'Frequent or severe flare-ups'],
      },
      {
        id: 'Q3',
        question: 'Do you experience pain or diarrhea?',
        subQuestion: 'GI symptom pattern',
        options: ['Mild intermittent symptoms', 'Frequent abdominal pain / diarrhea', 'Severe ongoing GI symptoms'],
      },
      {
        id: 'Q4',
        question: 'Are you on medication?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current medication', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Are symptoms worsening?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
    ],
  },
  {
    condition: 'Severe Arthritis (M19.90)',
    questions: [
      {
        id: 'Q1',
        question: 'Which joints are severely affected?',
        subQuestion: 'Symptom history and current functional impact',
        options: [
          'Neck',
          'Lower back',
          'Shoulder — Left',
          'Shoulder — Right',
          'Elbow — Left',
          'Elbow — Right',
          'Wrist — Left',
          'Wrist — Right',
          'Hand/Fingers — Left',
          'Hand/Fingers — Right',
          'Hip — Left',
          'Hip — Right',
          'Knee — Left',
          'Knee — Right',
          'Ankle — Left',
          'Ankle — Right',
          'Foot/Toes — Left',
          'Foot/Toes — Right',
          'Multiple joints throughout the body',
        ],
      },
      {
        id: 'Q2',
        question: 'How long have symptoms lasted?',
        subQuestion: 'Duration and chronicity',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q3',
        question: 'Do you have severe pain or swelling?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q4',
        question: 'Which treatments have you tried that did not provide enough relief?',
        subQuestion: 'Symptom history and current functional impact',
        options: [
          'Over-the-counter pain medication',
          'Prescription pain medication',
          'Anti-inflammatory medication',
          'Steroid medication',
          'Joint injections',
          'Physical therapy',
          'Occupational therapy',
          'Chiropractic treatment',
          'Surgery',
          'Other treatment',
        ],
      },
      {
        id: 'Q5',
        question: 'Does it limit mobility?',
        subQuestion: 'Functional impact',
        options: ['Mild limitation', 'Moderate limitation', 'Significant mobility limitation'],
      },
    ],
  },
  {
    condition: 'Fibromyalgia (M79.7)',
    questions: [
      {
        id: 'Q1',
        question: 'Where is your pain located?',
        subQuestion: 'Pain location and distribution',
        options: ['Localized to one area', 'Multiple areas involved', 'Widespread or radiating pain'],
      },
      {
        id: 'Q2',
        question: 'Do you have fatigue?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q3',
        question: 'How long have symptoms lasted?',
        subQuestion: 'Duration and chronicity',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q4',
        question: 'Are you on treatment?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current treatment', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Does it affect daily life?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
  {
    condition: "Alzheimer's Disease (G30.9)",
    questions: [
      {
        id: 'Q1',
        question: 'When were you diagnosed?',
        subQuestion: 'Diagnosis timeline',
        options: ['Diagnosed recently', 'Diagnosed over 1 year ago', 'Longstanding diagnosis'],
      },
      {
        id: 'Q2',
        question: 'Do you have memory loss?',
        subQuestion: 'Cognitive symptom severity',
        options: ['Mild forgetfulness', 'Moderate memory decline', 'Significant memory impairment'],
      },
      {
        id: 'Q3',
        question: 'Are you under care?',
        subQuestion: 'Level of active medical follow-up',
        options: ['Not in active treatment', 'Routine follow-up in place', 'Active specialist management'],
      },
      {
        id: 'Q4',
        question: 'Has condition progressed?',
        subQuestion: 'Disease progression',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
      {
        id: 'Q5',
        question: 'Does it affect daily functioning?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
  {
    condition: 'Severe Nausea (R11.0)',
    questions: [
      {
        id: 'Q1',
        question: 'How often do you feel nauseous?',
        subQuestion: 'Symptom frequency',
        options: ['Occasional episodes', 'Frequent episodes', 'Daily or near-daily symptoms'],
      },
      {
        id: 'Q2',
        question: 'Do you vomit frequently?',
        subQuestion: 'Vomiting frequency',
        options: ['Rare vomiting', 'Intermittent vomiting', 'Frequent vomiting'],
      },
      {
        id: 'Q3',
        question: 'Have you lost weight?',
        subQuestion: 'Presence of unintentional weight loss',
        options: ['No meaningful weight loss', 'Mild weight loss', 'Noticeable weight loss'],
      },
      {
        id: 'Q4',
        question: 'What treatments have you tried?',
        subQuestion: 'Prior treatment history',
        options: ['Conservative measures only', 'Medication trial(s)', 'Multiple treatments with limited relief'],
      },
      {
        id: 'Q5',
        question: 'Does it affect eating?',
        subQuestion: 'Functional impact',
        options: ['Mild effect on intake', 'Reduced intake most days', 'Significant difficulty maintaining intake'],
      },
    ],
  },
  {
    condition: 'Severe Muscle Spasms (M62.83)',
    questions: [
      {
        id: 'Q1',
        question: 'Where do spasms occur?',
        subQuestion: 'Spasm location and distribution',
        options: ['Single area involved', 'Several areas involved', 'Widespread muscle involvement'],
      },
      {
        id: 'Q2',
        question: 'How severe are they?',
        subQuestion: 'Symptom severity',
        options: ['Mild', 'Moderate', 'Severe'],
      },
      {
        id: 'Q3',
        question: 'How often do they happen?',
        subQuestion: 'Symptom frequency',
        options: ['Occasional episodes', 'Frequent episodes', 'Daily or near-daily symptoms'],
      },
      {
        id: 'Q4',
        question: 'Have treatments failed?',
        subQuestion: 'Failed treatment burden',
        options: ['Basic measures failed', 'Medication trial(s) failed', 'Multiple therapies failed'],
      },
      {
        id: 'Q5',
        question: 'Do they limit movement?',
        subQuestion: 'Movement limitation',
        options: ['Mild limitation', 'Moderate limitation', 'Significant movement limitation'],
      },
    ],
  },
  {
    condition: 'Peripheral Neuropathy (G62.9)',
    questions: [
      {
        id: 'Q1',
        question: 'Where do you feel symptoms?',
        subQuestion: 'Neuropathy distribution',
        options: ['Localized area only', 'Hands or feet primarily', 'Multiple limbs / progressive spread'],
      },
      {
        id: 'Q2',
        question: 'How long have you had them?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Less than 6 months', '6–12 months', 'More than 1 year'],
      },
      {
        id: 'Q3',
        question: 'How are the symptoms progressing?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Stable overall', 'Gradually worsening', 'Clearly progressive'],
      },
      {
        id: 'Q4',
        question: 'Are you on treatment?',
        subQuestion: 'Current medication or treatment status',
        options: ['No current treatment', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Do they affect mobility?',
        subQuestion: 'Functional impact',
        options: ['Mild limitation', 'Moderate limitation', 'Significant mobility limitation'],
      },
    ],
  },
  {
    condition: 'Cachexia (R64)',
    questions: [
      {
        id: 'Q1',
        question: 'Have you experienced weight loss?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q2',
        question: 'How much weight have you lost?',
        subQuestion: 'Magnitude of weight loss',
        options: ['Less than 5 lb', '5–10 lb', 'More than 10 lb'],
      },
      {
        id: 'Q3',
        question: 'Do you have appetite loss?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['Mild / limited', 'Moderate / recurring', 'Severe / persistent'],
      },
      {
        id: 'Q4',
        question: 'Are you under treatment?',
        subQuestion: 'Symptom history and current functional impact',
        options: ['No current treatment', 'On treatment with partial benefit', 'On treatment with ongoing symptoms'],
      },
      {
        id: 'Q5',
        question: 'Does it affect strength or energy?',
        subQuestion: 'Functional impact',
        options: ['Minimal functional impact', 'Moderate daily interference', 'Significant functional impairment'],
      },
    ],
  },
];

// Condition names only, for the secondary-condition picker (which doesn't
// ask follow-up questions).
export const ALL_MEDICAL_CONDITIONS_V2: string[] = GOOD_CANNA_V2_CONDITIONS.map(
  (conditionQuestionSet) => conditionQuestionSet.condition
);
